/**
 * The one place the image-generation pricing fields are built.
 *
 * POST /api/v1/image/generate and POST /api/v1/cost/preview describe the SAME
 * job, so the fields that decide its price have to be written once. They were
 * written twice: the cost card sent `num_steps: config.num_steps` while the
 * submit builder sent `num_steps: config.num_steps || 30`, and the six
 * OpenRouter image providers declare `defaultNumSteps: 0` — so picking any of
 * them made the card ask a price for 0 steps (rejected: "'num_steps' must be a
 * positive number, got 0.0") and the Generate button silently ask for 30.
 *
 * The 0 was never a step count. Those providers are plain HTTP calls billed at
 * a fixed amount per image; `supportsNumSteps: false` in
 * `constants/imageSource.ts` already says the knob does not apply to them. A
 * parameter that does not apply is OMITTED here — not sent as 0, and not
 * papered over with a 30 the user never chose — so the request carries exactly
 * the knobs the selected provider has, and the quote and the charge are
 * computed from the same object.
 */

import { getProviderFeatures, DEFAULT_AI_IMAGE_PROVIDER } from '../constants/imageSource';
import type { ImageGenerationConfig } from '../types';

/** The subset of POST /api/v1/image/generate that moves its price. */
export interface ImagePricingFields {
  provider: string;
  resolution?: string;
  width?: number;
  height?: number;
  num_images?: number;
  num_steps?: number;
  model?: string;
}

/**
 * The pricing-relevant fields of an image-generation request.
 *
 * Absent knobs are left out rather than defaulted here: the backend applies
 * `ImageGenerateRequest`'s own defaults, so the quote follows the schema
 * instead of a second set of numbers maintained in the client.
 *
 * @param config The form's current image configuration.
 * @returns The fields to send, as POST /api/v1/image/generate names them.
 */
export function buildImagePricingFields(
  config: Partial<ImageGenerationConfig>
): ImagePricingFields {
  const provider = config.provider || DEFAULT_AI_IMAGE_PROVIDER;
  const fields: ImagePricingFields = {
    provider,
    resolution: config.resolution ?? undefined,
    width: config.width ?? undefined,
    height: config.height ?? undefined,
    num_images: config.num_images ?? undefined,
    model: config.model ?? undefined,
  };
  // Steps only exist for the providers that render with them. For everything
  // else the field is not part of this request at all.
  if (getProviderFeatures(provider).supportsNumSteps && config.num_steps) {
    fields.num_steps = config.num_steps;
  }
  return fields;
}
